import events from '~/modules/game-events/game-events';
import { PingPongTracker } from '~/modules/network/rpc/ping-pong-tracker';
import { PlayerNumber } from '~/modules/players/player-number';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { SenderInterface } from '~/modules/remote-mic/network/server/transport/interface';
import { getPingTime } from '~/modules/remote-mic/network/utils';
import { RemoteMicPermission } from '~/routes/settings/settings-state';

/**
 * Unanswered pings a mic is allowed before the room stops counting on it. Each round is one ping
 * plus the wait for its pong, so three is about fifteen seconds of a phone saying nothing at all -
 * comfortably longer than a phone that is merely slow, or one riding out a reconnect of its own.
 */
const SILENT_ROUNDS_BEFORE_GONE = 3;

/**
 * How long the last pitch heard stands in for a phone whose packs have stopped. Packs come every
 * 50ms, so this rides out network jitter; past it the phone is treated as silent. Guessing on for
 * the whole of a dropout scored notes nobody was heard singing, and the next pack took them all
 * back - the singer's score fell to where the dropout began.
 */
const LAST_PITCH_STANDS_FOR_MS = 250;

/** A phone batches its readings every 50ms, so on average a reading waits half that before it is sent. */
const HALF_PACK_INTERVAL_MS = 25;
/** Round trips past this are a stall, not the network's usual delay, and are not averaged in. */
const MAX_TRUSTED_RTT_MS = 1_000;

/** How long a phone asked to listen may send nothing before it counts as silent: opening a mic takes a second or two. */
const SILENT_AFTER_MS = 3_000;

// Helper to send an rpc-call to a connected client (server-initiated call)
const sendRpcCall = (connection: SenderInterface, method: string, args: unknown[] = []) => {
  connection.send({ t: 'rpc-call', method, args } as NetworkMessages);
};

class RemoteMicInput {
  private frequencies: number[] | number[][] = [0];
  private volumes = [0];
  private lastPackAt = 0;
  /** When the game last asked this phone to listen, or null while it isn't. */
  private monitoringSince: number | null = null;

  private requestReadinessPromise: null | Promise<boolean> = null;

  public constructor(
    private connection: SenderInterface,
    private inputLag: number,
  ) {}

  getFrequencies = () => {
    if (!Array.isArray(this.frequencies[0]) && Date.now() - this.lastPackAt > LAST_PITCH_STANDS_FOR_MS) {
      return [0, 0];
    }

    return this.frequencies;
  };

  clearFrequencies = () => {
    if (Array.isArray(this.frequencies[0])) {
      this.frequencies = [this.frequencies[0].at(-1)!];
    }
  };
  getVolumes = () => this.volumes;

  getInputLag = () => {
    return this.inputLag;
  };

  setInputLag = (inputLag: number) => {
    this.inputLag = inputLag;
  };

  requestReadiness = (secondsLeft?: number) => {
    console.log('requestReadiness');
    if (!this.requestReadinessPromise) {
      this.requestReadinessPromise = new Promise<boolean>((resolve) => {
        const deviceId = this.connection.peer;

        const cleanup = () => {
          clearTimeout(timeoutId);
          events.readinessConfirmed.unsubscribe(handleReadinessConfirmed);
          events.remoteMicDisconnected.unsubscribe(handleDisconnect);
          this.requestReadinessPromise = null;
        };

        const handleReadinessConfirmed = (confirmedDeviceId: string) => {
          if (confirmedDeviceId === deviceId) {
            cleanup();
            resolve(true);
          }
        };

        const handleDisconnect = ({ id }: { id: string }) => {
          if (id === deviceId) {
            cleanup();
            resolve(false);
          }
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          resolve(false);
        }, 30_000);

        events.readinessConfirmed.subscribe(handleReadinessConfirmed);
        events.remoteMicDisconnected.subscribe(handleDisconnect);

        sendRpcCall(this.connection, 'requestReadiness', [secondsLeft]);
      });
    }
    return this.requestReadinessPromise!;
  };

  startMonitoring = async () => {
    this.connection?.off('data', this.handleRTCData);
    this.resetFrequencies();
    this.monitoringSince = Date.now();
    sendRpcCall(this.connection, 'startMonitor');

    this.connection?.on('data', this.handleRTCData);
  };

  stopMonitoring = async () => {
    sendRpcCall(this.connection, 'stopMonitor');

    this.detach();
  };

  /** Stops reading the phone without telling it - the connection is being dropped or replaced. */
  detach = () => {
    this.connection?.off('data', this.handleRTCData);
    this.resetFrequencies();
    this.monitoringSince = null;
  };

  /** Asked to listen, yet nothing heard for a while: its mic never opened, or stopped. */
  isSilent = (now = Date.now()) =>
    this.monitoringSince !== null && now - Math.max(this.lastPackAt, this.monitoringSince) > SILENT_AFTER_MS;

  // A backlog left from before would otherwise be replayed into the first update of the next song.
  private resetFrequencies = () => {
    this.frequencies = [0];
    this.volumes = [0];
    this.lastPackAt = 0;
  };

  private handleRTCData = (data: NetworkMessages) => {
    if (data.t === 'freq') {
      // Added to whatever the game has not read yet rather than replacing it. After a network stall
      // the packs arrive in one burst, and keeping only the last left the game to replay 50ms of
      // singing over the whole stall - the singer's score fell back to where the stall began.
      const unread = Array.isArray(this.frequencies[0]) ? this.frequencies[0] : [];
      const pack = [...unread, ...data[0]];
      this.frequencies = [pack, pack];
      this.volumes = [data[1], data[1]];
      this.lastPackAt = Date.now();
    }
  };
}

export class RemoteMic {
  private input: RemoteMicInput;
  /**
   * One loop per mic, and only one. It used to be a bare `setTimeout` re-armed on every pong
   * without clearing the last one, so a pong that arrived twice - which is what a host whose
   * transport listener had been registered twice did to every pong - left two timers running,
   * then four, then eight. The tracker keeps a single timer and ignores any pong it did not ask
   * for, and its watchdog re-arms the loop if a pong never comes back at all.
   */
  private pingPong = new PingPongTracker({
    intervalMs: 1_000,
    maxWaitMs: 5_000,
    initialLatency: 9999,
    onMissedPong: () => this.handleMissedPong(),
  });

  private missedPongs = 0;
  /** Smoothed ping round trip, or null before the first pong. */
  private smoothedRtt: number | null = null;

  constructor(
    public id: string,
    public name: string,
    public connection: SenderInterface,
    lag: number,
    /** Called when the phone has stopped answering for good - see `SILENT_ROUNDS_BEFORE_GONE`. */
    private onSilent?: (id: string) => void,
  ) {
    this.input = new RemoteMicInput(connection, lag);

    this.pingPong.start(this.pingClient);
  }

  public getInput = () => this.input;

  public setPlayerNumber = (playerNumber: PlayerNumber | null) => {
    sendRpcCall(this.connection, 'setPlayerNumber', [playerNumber]);
  };

  public onDisconnect = () => {
    this.pingPong.stop();
    this.input.detach();
  };

  /**
   * Stops asking. Used while the game itself is off the relay: nothing this mic sends can arrive
   * then, and counting that silence against the phone would empty the room of phones that are fine.
   */
  public pause = () => {
    this.pingPong.stop();
  };

  public resume = () => {
    this.missedPongs = 0;
    this.pingPong.start(this.pingClient);
  };

  private handleMissedPong = () => {
    this.missedPongs++;
    if (this.missedPongs < SILENT_ROUNDS_BEFORE_GONE) return;

    this.pingPong.stop();
    this.onSilent?.(this.id);
  };

  public setPermission = (level: RemoteMicPermission) => {
    sendRpcCall(this.connection, 'setPermissions', [level]);
  };

  public onPong = () => {
    this.missedPongs = 0;
    this.pingPong.handlePong();

    const rtt = this.pingPong.getLatency();
    if (rtt < MAX_TRUSTED_RTT_MS)
      this.smoothedRtt = this.smoothedRtt === null ? rtt : this.smoothedRtt * 0.8 + rtt * 0.2;
  };

  /** How late a reading reaches the game after it was sung: waiting for its pack, then one way across. */
  public getTransitDelay = () => HALF_PACK_INTERVAL_MS + (this.smoothedRtt ?? 0) / 2;

  private pingClient = () => {
    this.connection.send({ t: 'ping' } as NetworkMessages);
  };

  public getLatency = () => {
    const pingStartedAt = this.pingPong.getPingStartedAt();

    return pingStartedAt
      ? Math.max(this.pingPong.getLatency(), getPingTime() - pingStartedAt)
      : this.pingPong.getLatency();
  };

  public getPingTime = () => this.pingPong.getPingStartedAt();
}
